/**
 * FileService - Handles file operations via the File Metadata API
 * 
 * Uses DynamoDB for metadata (O(1) rename/move operations) and S3 for file content.
 * Falls back to legacy S3-only mode for files not yet migrated.
 * 
 * @module FileService
 */

import * as fileMetadataApi from './fileMetadataApi';
import * as LegacyFileService from './LegacyFileService';
import MultipartUploader from './MultipartUploader';

// Threshold for using multipart upload (10MB)
const MULTIPART_THRESHOLD = 10 * 1024 * 1024;

/**
 * Progress callback type for tracking operation progress
 * @callback ProgressCallback
 * @param {number} progress - Progress percentage (0-100)
 */

/**
 * Check if an identifier is a legacy S3 key (path-based) vs a DynamoDB ID
 * Legacy keys contain '/' or '.' (file extensions), IDs are hex strings
 * @param {string} identifier - The file identifier
 * @returns {boolean} True if this looks like a legacy S3 key
 */
const isLegacyKey = (identifier) => {
  if (!identifier) return false;
  // Legacy keys are paths like "folder/file.txt" or "file.txt"
  // DynamoDB IDs are 16-char hex strings like "a1b2c3d4e5f6g7h8"
  return identifier.includes('/') || identifier.includes('.');
};

/**
 * Upload a file to S3 via the metadata API
 * 
 * Automatically selects between simple upload (for files <= 10MB) and
 * multipart upload (for files > 10MB) for optimal performance.
 * 
 * Simple upload flow:
 * 1. Call API to get presigned upload URL (creates PENDING record)
 * 2. Upload file directly to S3 using presigned URL
 * 3. Confirm upload completion via API (sets status to COMPLETE)
 * 
 * Multipart upload flow:
 * 1. Initiate multipart upload (creates PENDING record with uploadId)
 * 2. Upload file in parallel chunks (5-10MB each)
 * 3. Complete multipart upload (assembles parts, sets status to COMPLETE)
 * 
 * @param {File} file - The file to upload
 * @param {string} path - The folder path to upload to (or folder ID)
 * @param {ProgressCallback} [onProgress] - Optional progress callback
 * @returns {Promise<Object>} The created file item
 * @throws {Error} If the upload fails
 */
export const upload = async (file, path, onProgress) => {
  if (!file) {
    throw new Error('File is required');
  }

  try {
    // Use multipart upload for large files
    if (file.size > MULTIPART_THRESHOLD) {
      const uploader = new MultipartUploader({
        file,
        parentFolderId: path || 'ROOT',
        onProgress
      });
      return await uploader.upload();
    }

    // Use simple upload for small files
    // Step 1: Get presigned upload URL from API
    const { uploadUrl, itemId } = await fileMetadataApi.getUploadUrl({
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
      parentFolderId: path || 'ROOT'
    });

    try {
      // Step 2: Upload file directly to S3 using presigned URL
      await uploadToS3(uploadUrl, file, onProgress);
    } catch (s3Error) {
      // Clean up the PENDING DynamoDB record if S3 upload fails
      console.error('S3 upload failed, cleaning up PENDING record:', itemId);
      try {
        await fileMetadataApi.deleteItem(itemId);
      } catch (cleanupError) {
        console.error('Failed to clean up PENDING record:', cleanupError);
      }
      throw s3Error;
    }

    // Step 3: Confirm upload completion
    const item = await fileMetadataApi.confirmUpload(itemId);

    return item;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error(`Failed to upload file "${file.name}": ${error.message}`);
  }
};

/**
 * Upload file to S3 using presigned URL with progress tracking
 * @param {string} url - Presigned PUT URL
 * @param {File} file - File to upload
 * @param {ProgressCallback} [onProgress] - Progress callback
 */
const uploadToS3 = async (url, file, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress(percentComplete);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('S3 upload failed'));
    });
    
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
};

/**
 * Download a file from S3 via the metadata API
 * Falls back to legacy S3 download for unmigrated files
 * 
 * @param {string} identifier - The item ID or legacy S3 key
 * @returns {Promise<void>}
 * @throws {Error} If the download fails
 */
export const download = async (identifier) => {
  if (!identifier) {
    throw new Error('Item ID or key is required');
  }

  try {
    // Check if this is a legacy S3 key
    if (isLegacyKey(identifier)) {
      console.log('Using legacy download for:', identifier);
      return await LegacyFileService.download(identifier);
    }

    // Use new API
    const { downloadUrl, fileName } = await fileMetadataApi.getDownloadUrl(identifier);

    // Create a temporary anchor element to trigger download
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = fileName || 'download';
    a.style.display = 'none';

    document.body.appendChild(a);
    a.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
    }, 100);

  } catch (error) {
    // If API fails with 404, try legacy fallback
    if (error.status === 404 && !isLegacyKey(identifier)) {
      console.log('Item not found in API, checking if legacy key exists');
      // This shouldn't happen for non-legacy keys, but handle gracefully
    }
    console.error('Error downloading file:', error);
    throw new Error(`Failed to download file: ${error.message}`);
  }
};

/**
 * Rename a file (O(1) operation - only updates DynamoDB metadata)
 * Legacy files cannot be renamed with O(1) - they need migration first
 * 
 * @param {string} id - The item ID of the file
 * @param {string} newName - The new file name
 * @returns {Promise<Object>} The updated item
 * @throws {Error} If the rename operation fails
 */
export const renameFile = async (id, newName) => {
  if (!id) {
    throw new Error('Item ID is required');
  }

  if (!newName) {
    throw new Error('New name is required');
  }

  // Legacy files cannot use O(1) rename - they need to be migrated first
  if (isLegacyKey(id)) {
    throw new Error('Legacy files must be migrated before renaming. Please run the migration script.');
  }

  try {
    return await fileMetadataApi.updateItem(id, { name: newName });
  } catch (error) {
    console.error('Error renaming file:', error);
    throw new Error(`Failed to rename file: ${error.message}`);
  }
};

/**
 * Move a file to a different folder (O(1) operation - only updates DynamoDB metadata)
 * Legacy files cannot be moved with O(1) - they need migration first
 * 
 * @param {string} id - The item ID of the file
 * @param {string} destinationFolderId - The destination folder ID
 * @returns {Promise<Object>} The updated item
 * @throws {Error} If the move operation fails
 */
export const moveFile = async (id, destinationFolderId) => {
  if (!id) {
    throw new Error('Item ID is required');
  }

  // Legacy files cannot use O(1) move - they need to be migrated first
  if (isLegacyKey(id)) {
    throw new Error('Legacy files must be migrated before moving. Please run the migration script.');
  }

  try {
    return await fileMetadataApi.updateItem(id, { 
      parentFolderId: destinationFolderId || 'ROOT' 
    });
  } catch (error) {
    console.error('Error moving file:', error);
    throw new Error(`Failed to move file: ${error.message}`);
  }
};

/**
 * Copy a file (creates new metadata record pointing to same S3 object)
 * Note: This creates a logical copy - both items reference the same S3 blob
 * 
 * @param {string} id - The item ID of the source file
 * @param {string} destinationFolderId - The destination folder ID
 * @param {string} [newName] - Optional new name for the copy
 * @returns {Promise<Object>} The new item
 * @throws {Error} If the copy operation fails
 */
export const copyFile = async (id, destinationFolderId, newName) => {
  if (!id) {
    throw new Error('Item ID is required');
  }

  // Note: Copy is not yet implemented in the backend
  // For now, throw an error indicating this
  throw new Error('Copy operation is not yet supported in the new architecture');
};

/**
 * Delete a file
 * Falls back to legacy S3 delete for unmigrated files
 * 
 * @param {string} identifier - The item ID or legacy S3 key
 * @returns {Promise<void>}
 * @throws {Error} If the delete fails
 */
export const deleteFile = async (identifier) => {
  if (!identifier) {
    throw new Error('Item ID or key is required');
  }

  try {
    // Check if this is a legacy S3 key
    if (isLegacyKey(identifier)) {
      console.log('Using legacy delete for:', identifier);
      return await LegacyFileService.deleteFile(identifier);
    }

    // Use new API
    await fileMetadataApi.deleteItem(identifier);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw new Error(`Failed to delete file: ${error.message}`);
  }
};

/**
 * Check if a file identifier is a legacy S3 key
 * Useful for UI to show migration prompts
 * @param {string} identifier - The file identifier
 * @returns {boolean} True if legacy
 */
export const isLegacy = isLegacyKey;

// Export as a service object for interface compatibility
const FileService = {
  upload,
  download,
  delete: deleteFile,
  rename: renameFile,
  move: moveFile,
  copy: copyFile,
  isLegacy,
  MULTIPART_THRESHOLD
};

export default FileService;
